import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { apiClient } from './api';
import { FeatureFlagsContext, type FeatureFlagsState } from './FeatureFlags';
import {
  getTenant,
  getTenantFromViewParam,
  resolveDocumentTitle,
  resolveFaviconUrl,
  type TenantConfig,
} from './config/tenants';
import { derivePanelId, type PanelId } from './config/panels';
import {
  captureInitialParamsFromUrl,
  getInitialParam,
  getInitialParams,
} from './utils/initialParams';

// `?view=<tenantId>` (e.g. `ario`, `q11`) needs to land in sessionStorage
// before we read it here. main.tsx also calls captureInitialParamsFromUrl,
// but its hoisted `import { App }` pulls in this module first — on a cold
// load that runs before main.tsx's own capture call, so sessionStorage would
// still be empty. Call it here too; it's idempotent (no-ops once the URL is
// already stripped) so the later call in main.tsx is a harmless no-op.
captureInitialParamsFromUrl();
const VIEW_TENANT_OVERRIDE = getTenantFromViewParam(getInitialParam('view'));

// Server inlines the active tenant on window before the bundle loads
// (see server.ts buildTenantIndexHtml) — use it as the initial state so
// branding is correct on first paint, with no default→tenant flash.
// A `?view=` override wins over whatever server-side tenant is active.
const INITIAL_TENANT: TenantConfig =
  VIEW_TENANT_OVERRIDE ||
  (typeof window !== 'undefined' &&
    (window as unknown as { __TAP_TENANT__?: TenantConfig }).__TAP_TENANT__) ||
  getTenant('default');

// Params are captured once into sessionStorage (see captureInitialParamsFromUrl
// above), so this stays the same for the whole session.
const INITIAL_PANEL_ID: PanelId | null = derivePanelId(getInitialParams());

// Apply tenant branding to the document: theme colors, title, favicon.
const applyTenant = (tenant: TenantConfig, panelId: PanelId | null): void => {
  if (typeof document === 'undefined') return;
  for (const [key, value] of Object.entries(tenant.theme)) {
    document.documentElement.style.setProperty(key, value);
  }
  document.title = resolveDocumentTitle(tenant, panelId);
  const favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (favicon) favicon.href = resolveFaviconUrl(tenant, panelId);
};

type FeaturesResponse = {
  [key: string]: unknown;
};

const normalizeFeatureFlags = (
  flags: Record<string, unknown>
): Record<string, boolean> => {
  return Object.entries(flags).reduce<Record<string, boolean>>(
    (acc, [key, value]) => {
      // Skip non-primitive payloads (enabled_brands array, tenant object).
      if (value !== null && typeof value === 'object') return acc;
      const normalizedKey = key.trim().toLowerCase();
      if (!normalizedKey) return acc;
      acc[normalizedKey] = Boolean(value);
      return acc;
    },
    {}
  );
};

type FetchedFlags = {
  flags: Record<string, boolean>;
  enabledRetailers: string[];
  tenant: TenantConfig;
};

const fetchFeatureFlags = async (): Promise<FetchedFlags> => {
  try {
    const json = (await apiClient.getFeatureFlags()) as FeaturesResponse;
    const serverTenant =
      json.tenant && typeof json.tenant === 'object'
        ? (json.tenant as TenantConfig)
        : INITIAL_TENANT;
    // `?view=` override wins over whatever tenant the server resolved.
    const tenant = VIEW_TENANT_OVERRIDE || serverTenant;
    const enabledRetailers = Array.isArray(json.enabled_brands)
      ? (json.enabled_brands as string[])
      : tenant.enabledRetailers;
    return { flags: normalizeFeatureFlags(json), enabledRetailers, tenant };
  } catch {
    return {
      flags: {},
      enabledRetailers: INITIAL_TENANT.enabledRetailers,
      tenant: INITIAL_TENANT,
    };
  }
};

type FeatureFlagsProviderProps = {
  children: ReactNode;
};

export const FeatureFlagsProvider = ({
  children,
}: FeatureFlagsProviderProps) => {
  const [state, setState] = useState<FeatureFlagsState>({
    flags: {},
    enabledRetailers: INITIAL_TENANT.enabledRetailers,
    tenant: INITIAL_TENANT,
  });

  useEffect(() => {
    let isMounted = true;

    void (async () => {
      const { flags, enabledRetailers, tenant } = await fetchFeatureFlags();
      if (!isMounted) return;

      applyTenant(tenant, INITIAL_PANEL_ID);
      setState({
        flags,
        enabledRetailers,
        tenant,
      });
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  const value = useMemo(
    () => ({
      flags: state.flags,
      enabledRetailers: state.enabledRetailers,
      tenant: state.tenant,
    }),
    [state.flags, state.enabledRetailers, state.tenant]
  );

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};
